import React, { useState } from 'react';
import { PlusCircle, Edit, Trash2 } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import SortableTable from '../../../components/ui/SortableTable';
import DirectorForm from '../forms/DirectorForm';
import ApiService from '../../../services/apiService';
import formatDate from '../utils/dateFormatter';

const DirectorsPage = ({ directors, onDataRefresh, addMessage, selectedCompany, userRole }) => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [editingDirector, setEditingDirector] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);

    const handleCreateDirector = async (data) => {
        if (!selectedCompany) {
            addMessage("No company selected.", 'error');
            return;
        }
        try {
            await ApiService.createDirector({ ...data, company_id: selectedCompany.id });
            onDataRefresh();
            setShowCreateModal(false);
            addMessage('Director created successfully!', 'success');
        } catch (error) {
            console.error('Error creating director:', error);
            console.error('Error details:', {
                message: error.message,
                error: error.error,
                code: error.code,
                details: error.details,
                hint: error.hint
            });
            addMessage(`Error creating director: ${error.message}`, 'error');
        }
    };

    const handleUpdateDirector = async (id, data) => {
        try {
            await ApiService.updateDirector(id, data);
            onDataRefresh();
            setShowEditModal(false);
            setEditingDirector(null);
            addMessage('Director updated successfully!', 'success');
        } catch (error) {
            addMessage(`Error updating director: ${error.message}`, 'error');
        }
    };

    const handleDeleteClick = (director) => {
        setItemToDelete(director);
        setShowConfirmDelete(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            await ApiService.deleteDirector(itemToDelete.id);
            onDataRefresh();
            setShowConfirmDelete(false);
            setItemToDelete(null);
            addMessage('Director deleted successfully!', 'success');
        } catch (error) {
            addMessage(`Error deleting director: ${error.message}`, 'error');
        }
    };

    const handleEditClick = (director) => {
        setEditingDirector(director);
        setShowEditModal(true);
    };

    const getDirectorTypes = (director) => {
        const types = [];
        if (director.is_director) types.push('Director');
        if (director.is_alternate_director) types.push('Alternate Director');
        if (director.is_company_secretary) types.push('Company Secretary');
        return types.length > 0 ? types.join(', ') : 'N/A';
    };

    const directorsTableColumns = [
        { 
            key: 'name', 
            header: 'Name', 
            isSortable: true,
            render: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'N/A'
        },
        { 
            key: 'type', 
            header: 'Type', 
            isSortable: false,
            render: (row) => getDirectorTypes(row)
        },
        { key: 'email', header: 'Email', isSortable: true },
        { 
            key: 'date_appointed', 
            header: 'Appointed', 
            isSortable: true, 
            type: 'date',
            render: (row) => formatDate(row.date_appointed)
        },
        { 
            key: 'date_end', 
            header: 'End Date', 
            isSortable: true, 
            type: 'date',
            render: (row) => row.date_end ? formatDate(row.date_end) : 'Current'
        },
        { key: 'status', header: 'Status', isSortable: true },
        { 
            key: 'actions', 
            header: 'Actions', 
            isSortable: false,
            render: (row) => (
                userRole !== 'viewer' && (
                    <div className="flex space-x-2">
                        <button
                            onClick={() => handleEditClick(row)}
                            className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                            <Edit className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => handleDeleteClick(row)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                )
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Directors</h2>
                {userRole !== 'viewer' && (
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        <PlusCircle className="h-5 w-5 mr-2" />
                        Add Director
                    </button>
                )}
            </div>

            {!selectedCompany ? (
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                    Please select a company to view directors.
                </div>
            ) : (
                <SortableTable
                    data={directors}
                    columns={directorsTableColumns}
                    entityType="director"
                    addMessage={addMessage}
                />
            )}

            {showCreateModal && (
                <Modal onClose={() => setShowCreateModal(false)} title="Add Director">
                    <DirectorForm
                        onSubmit={handleCreateDirector}
                        onCancel={() => setShowCreateModal(false)}
                    />
                </Modal>
            )}

            {showEditModal && editingDirector && (
                <Modal onClose={() => setShowEditModal(false)} title="Edit Director">
                    <DirectorForm
                        initialData={editingDirector}
                        onSubmit={(data) => handleUpdateDirector(editingDirector.id, data)}
                        onCancel={() => {
                            setShowEditModal(false);
                            setEditingDirector(null);
                        }}
                    />
                </Modal>
            )}

            {showConfirmDelete && (
                <Modal onClose={() => setShowConfirmDelete(false)} title="Confirm Delete">
                    <div className="space-y-4">
                        <p className="text-gray-700">
                            Are you sure you want to delete this director? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-2">
                            <button
                                type="button"
                                onClick={() => setShowConfirmDelete(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmDelete}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                            >
                                Confirm Delete
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default DirectorsPage;